import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, RefreshCw, ChevronDown, ChevronRight, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn } from '@/lib/utils'
import { getTopicMessages } from '../queries/topicMessages'
import type { TopicMessage } from '../types/topology'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtTime(epochMs: number) {
  const d = new Date(epochMs)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  const ms = String(d.getMilliseconds()).padStart(3, '0')
  return `${hh}:${mm}:${ss}.${ms}`
}

function fmtDate(epochMs: number) {
  return new Date(epochMs).toLocaleDateString()
}

/** Try multiple field name variants and return the first match. */
function pick(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return String(obj[k])
  }
  return ''
}

/** Recursively parse any string values that look like JSON objects/arrays. */
function deepParse(value: unknown): unknown {
  if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
    try { return deepParse(JSON.parse(value)) } catch { /* not JSON */ }
  }
  if (Array.isArray(value)) return value.map(deepParse)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, deepParse(v)])
    )
  }
  return value
}

/** Extract displayable tag chips from a message value. */
interface Tag { label: string; value: string; color: string }

function extractTags(value: unknown): Tag[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []
  const o = value as Record<string, unknown>
  const tags: Tag[] = []

  const add = (label: string, color: string, ...keys: string[]) => {
    const v = pick(o, ...keys)
    if (v) tags.push({ label, value: v, color })
  }

  add('accId',    'bg-blue-100 text-blue-700',    'accountId', 'accId', 'acc_id', 'account_id')
  add('campaign', 'bg-purple-100 text-purple-700', 'campaignId', 'campaign_id')
  add('exec',     'bg-indigo-100 text-indigo-700', 'executionId', 'execution_id')
  add('channel',  channelColor(pick(o, 'channelType', 'channel_type', 'channel')),
                  'channelType', 'channel_type', 'channel')
  add('type',     'bg-gray-100 text-gray-600',     'messageType', 'message_type', 'type',
                  'eventName', 'event_name', 'eventType', 'event_type')
  add('user',     'bg-orange-100 text-orange-700', 'userId', 'user_id', 'uid')
  add('vendor',   'bg-teal-100 text-teal-700',     'vendor')
  add('test',     pick(o, 'testMode') === 'true' ? 'bg-yellow-100 text-yellow-700' : '',
                  'testMode')

  return tags.filter(t => t.color) // drop empty-color entries
}

function channelColor(ch: string): string {
  switch (ch.toUpperCase()) {
    case 'RCS':       return 'bg-cyan-100 text-cyan-700'
    case 'SMS':       return 'bg-yellow-100 text-yellow-700'
    case 'EMAIL':     return 'bg-sky-100 text-sky-700'
    case 'WHATSAPP':  return 'bg-green-100 text-green-700'
    case 'PUSH':      return 'bg-rose-100 text-rose-700'
    default:          return 'bg-gray-100 text-gray-600'
  }
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

interface Filters {
  search:      string
  accountId:   string
  campaignId:  string
  executionId: string
  channelType: string
  userId:      string
}

const EMPTY_FILTERS: Filters = {
  search: '', accountId: '', campaignId: '', executionId: '', channelType: '', userId: '',
}

function FilterBar({ filters, onChange }: { filters: Filters; onChange: (f: Filters) => void }) {
  const set  = (k: keyof Filters) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...filters, [k]: e.target.value })
  const clr  = (k: keyof Filters) => onChange({ ...filters, [k]: '' })
  const hasAny = Object.values(filters).some(Boolean)

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <SearchInput value={filters.search}      onChange={set('search')}      onClear={() => clr('search')}
        placeholder="Search key / value…" width="w-52" icon />

      <FilterChip label="accountId"   value={filters.accountId}   onChange={set('accountId')}   onClear={() => clr('accountId')} />
      <FilterChip label="campaignId"  value={filters.campaignId}  onChange={set('campaignId')}  onClear={() => clr('campaignId')} />
      <FilterChip label="executionId" value={filters.executionId} onChange={set('executionId')} onClear={() => clr('executionId')} />
      <FilterChip label="channelType" value={filters.channelType} onChange={set('channelType')} onClear={() => clr('channelType')} />
      <FilterChip label="userId"      value={filters.userId}      onChange={set('userId')}      onClear={() => clr('userId')} />

      {hasAny && (
        <button onClick={() => onChange(EMPTY_FILTERS)}
          className="text-xs text-gray-400 hover:text-gray-600 underline ml-1">
          clear all
        </button>
      )}
    </div>
  )
}

function SearchInput({ value, onChange, onClear, placeholder, width, icon }: {
  value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClear: () => void; placeholder: string; width: string; icon?: boolean
}) {
  return (
    <div className={cn('relative', width)}>
      {icon && <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />}
      <input type="text" value={value} onChange={onChange} placeholder={placeholder}
        className={cn(
          'pr-7 py-1.5 text-xs border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-1 focus:ring-blue-300',
          icon ? 'pl-7' : 'pl-3'
        )} />
      {value && (
        <button onClick={onClear} className="absolute right-2 top-1/2 -translate-y-1/2">
          <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
  )
}

function FilterChip({ label, value, onChange, onClear }: {
  label: string; value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; onClear: () => void
}) {
  return (
    <div className="relative flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-blue-300">
      <span className="pl-2.5 pr-1 text-[10px] text-gray-400 font-mono select-none whitespace-nowrap">
        {label}
      </span>
      <span className="text-[10px] text-gray-300">=</span>
      <input type="text" value={value} onChange={onChange} placeholder="any"
        className="pl-1 pr-6 py-1.5 text-xs w-24 focus:outline-none bg-transparent" />
      {value && (
        <button onClick={onClear} className="absolute right-2 top-1/2 -translate-y-1/2">
          <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
  )
}

// ─── Message row ─────────────────────────────────────────────────────────────

function MessageRow({ msg, index }: { msg: TopicMessage; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const isEven = index % 2 === 0
  const tags   = extractTags(msg.value)

  // Derive the message ID for display
  const msgId = msg.key ?? (
    msg.value && typeof msg.value === 'object'
      ? pick(msg.value as Record<string, unknown>, 'messageId', 'id', 'eventId', 'requestId')
      : ''
  )

  const parsedValue = useMemo(() => deepParse(msg.value), [msg.value])

  return (
    <>
      <tr
        className={cn(
          'cursor-pointer hover:bg-blue-50/60 transition-colors border-b border-gray-100',
          isEven ? 'bg-white' : 'bg-gray-50/40',
          expanded && '!bg-blue-50/70'
        )}
        onClick={() => setExpanded(v => !v)}
      >
        {/* Expand chevron */}
        <td className="pl-3 pr-1 py-2.5 w-5">
          {expanded
            ? <ChevronDown  className="h-3.5 w-3.5 text-blue-400" />
            : <ChevronRight className="h-3.5 w-3.5 text-gray-300" />}
        </td>

        {/* Timestamp */}
        <td className="px-3 py-2.5 whitespace-nowrap w-36">
          <p className="text-xs font-mono text-gray-800">{fmtTime(msg.timestamp)}</p>
          <p className="text-[10px] text-gray-400">{fmtDate(msg.timestamp)}</p>
        </td>

        {/* Partition / Offset */}
        <td className="px-3 py-2.5 whitespace-nowrap w-24">
          <span className="text-[11px] font-mono text-gray-400">
            p{msg.partition} / {msg.offset}
          </span>
        </td>

        {/* Message ID */}
        <td className="px-3 py-2.5 w-36">
          <span className="text-[11px] font-mono text-purple-700 truncate block max-w-[130px]" title={msgId}>
            {msgId || <span className="text-gray-300">—</span>}
          </span>
        </td>

        {/* Tag chips */}
        <td className="px-3 py-2.5">
          <div className="flex flex-wrap gap-1">
            {tags.map(t => (
              <span key={t.label}
                className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded', t.color)}>
                {t.label === 'test' ? (t.value === 'true' ? 'TEST' : '') : t.value.length > 18 ? t.value.slice(0, 16) + '…' : t.value}
              </span>
            ))}
            {tags.length === 0 && (
              <span className="text-[11px] text-gray-400 font-mono truncate max-w-xs">
                {typeof msg.value === 'string'
                  ? msg.value.slice(0, 80)
                  : JSON.stringify(msg.value).slice(0, 80)}
                {JSON.stringify(msg.value ?? '').length > 80 && '…'}
              </span>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded JSON viewer */}
      {expanded && (
        <tr className={isEven ? 'bg-white' : 'bg-gray-50/40'}>
          <td colSpan={5} className="px-5 pb-4 pt-1">
            <pre className="text-[11px] bg-gray-950 text-green-300 p-4 rounded-xl overflow-auto max-h-96 leading-relaxed border border-gray-800 font-mono">
              {JSON.stringify(parsedValue, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TopicMessagesPage() {
  const { topicName = '' } = useParams<{ topicName: string }>()
  const navigate = useNavigate()

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)

  const { data = [], isLoading, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['topic-messages', topicName],
    queryFn: () => getTopicMessages(topicName, 500),
    staleTime: 0,
  })

  const filtered = useMemo(() => {
    return data.filter(msg => {
      const str = (msg.key ?? '') + JSON.stringify(msg.value ?? '')

      if (filters.search && !str.toLowerCase().includes(filters.search.toLowerCase())) return false

      if (filters.accountId || filters.campaignId || filters.executionId ||
          filters.channelType || filters.userId) {
        if (!msg.value || typeof msg.value !== 'object' || Array.isArray(msg.value)) return false
        const o = msg.value as Record<string, unknown>

        if (filters.accountId) {
          const v = pick(o, 'accountId', 'accId', 'acc_id', 'account_id')
          if (!v.toLowerCase().includes(filters.accountId.toLowerCase())) return false
        }
        if (filters.campaignId) {
          const v = pick(o, 'campaignId', 'campaign_id')
          if (!v.toLowerCase().includes(filters.campaignId.toLowerCase())) return false
        }
        if (filters.executionId) {
          const v = pick(o, 'executionId', 'execution_id')
          if (!v.toLowerCase().includes(filters.executionId.toLowerCase())) return false
        }
        if (filters.channelType) {
          const v = pick(o, 'channelType', 'channel_type', 'channel')
          if (!v.toLowerCase().includes(filters.channelType.toLowerCase())) return false
        }
        if (filters.userId) {
          const v = pick(o, 'userId', 'user_id', 'uid')
          if (!v.toLowerCase().includes(filters.userId.toLowerCase())) return false
        }
      }

      return true
    })
  }, [data, filters])

  const hasFilters = Object.values(filters).some(Boolean)

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/pipeline-topology')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Pipeline Health
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-mono font-semibold text-gray-900">{topicName}</span>
          <span className="text-[11px] bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full font-medium">
            last 500 · newest first
          </span>
        </div>
        <div className="flex items-center gap-3">
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-gray-400">
              fetched {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
        <FilterBar filters={filters} onChange={setFilters} />
      </div>

      {isLoading && <LoadingState />}
      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load messages'}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* Table toolbar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/80">
            <span className="text-xs text-gray-500">
              {hasFilters
                ? <><span className="font-medium text-gray-800">{filtered.length}</span> of {data.length} messages</>
                : <><span className="font-medium text-gray-800">{data.length}</span> messages</>}
            </span>
            <span className="text-[10px] text-gray-400">click row to expand full JSON · nested strings auto-parsed</span>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-400">
              {data.length === 0 ? 'No messages in topic' : 'No messages match the current filters'}
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10 bg-white border-b border-gray-100">
                  <tr>
                    <th className="pl-3 pr-1 py-2 w-5" />
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Time</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">P / Offset</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">ID / Key</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((msg, i) => (
                    <MessageRow
                      key={`${msg.partition}-${msg.offset}`}
                      msg={msg}
                      index={i}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
